import React from 'react';
import { withRouter } from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { withTranslation } from 'react-i18next';
import { ContainerBox, Table, ModalFooter } from 'components';
import { MODAL_ID } from '../../core/utils/constants';
import BaseComponent from '../BaseComponent';
import './style.scss';

@inject('commonStore', 'modalStore')
@observer
class EditPostProcessing extends BaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      validationErrors: {},
      regexColumnWidth: 40, // percentage
      replacementColumnWidth: 40 // percentage
    };
    this.isEditing = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartRegexWidth = 0;
    this.dragStartReplacementWidth = 0;
    this.tooltipDebounceTimer = null;
    this.previousRowCount = 0;
  }

  calcCellWidth(txt) {
    var span = document.createElement('span');
    span.style.position = 'absolute';
    span.style.top = '-1000px';
    span.style.left = '-1000px';
    span.style.whiteSpace = 'nowrap';
    span.innerHTML = txt;
    span.style.fontFamily = '"MS PGothic",Osaka,Arial,sans-serif';
    span.style.fontSize = '14px';
    document.body.appendChild(span);
    var width = span.clientWidth + 24;
    span.parentElement.removeChild(span);
    return width;
  }

  calcHeaderWidth(txt) {
    var span = document.createElement('span');
    span.style.position = 'absolute';
    span.style.top = '-1000px';
    span.style.left = '-1000px';
    span.style.whiteSpace = 'nowrap';
    span.innerHTML = txt;
    span.style.fontFamily = '"Noto Sans JP",sans-serif';
    span.style.fontSize = '14px';
    span.style.fontWeight = 'bold';
    document.body.appendChild(span);
    var width = span.clientWidth + 22;
    span.parentElement.removeChild(span);
    return width;
  }

  adjustWidth = () => {
    const { t, commonStore: { postProcessingStore: { editProcess } } } = this.props;
    const tableElement = document.querySelector('.resizable-post-processing-table table');
    if (!tableElement) return;
    const tableWidth = tableElement.offsetWidth;
    const maxCombinedWidth = this.state.regexColumnWidth + this.state.replacementColumnWidth;
    const minWidth = 12;

    let regexWidth = this.calcHeaderWidth(t("post_processing.edit.table.regex_pattern"));
    editProcess.setting_body.forEach((row) => {
      let width = this.calcCellWidth(row.regex);
      if (width > regexWidth) {
        regexWidth = width;
      }
    });
    let regexPercentage = 100 * regexWidth / tableWidth;
    if (regexPercentage < minWidth) {
      regexPercentage = minWidth;
    } else if (regexPercentage > maxCombinedWidth - minWidth) {
      regexPercentage = maxCombinedWidth - minWidth;
    }
    let replacementPercentage = maxCombinedWidth - regexPercentage;
    this.setState({
      regexColumnWidth: regexPercentage,
      replacementColumnWidth: replacementPercentage
    });
  }

  executeLogicAfterRender = () => {
    this.props.commonStore.postProcessingStore.clean();
    let id = this.props.match.params?.id;
    if (id) {
      this.props.commonStore.postProcessingStore.getPostProcessById(id, this.adjustWidth);
    }
  }

  executeLogicBeforeOutComponent = () => {
    this.props.commonStore.postProcessingStore.clean();
  }

  handleBack = () => {
    if (this.isEditing) {
      this.openBackConfirmModal();
    } else {
      this.props.history.push("/postprocessing");
    }
  }

  openBackConfirmModal = () => {
    const { modalStore, t } = this.props;
    modalStore.show({
      id: MODAL_ID.CONFIRM,
      isOpen: true,
      header: t("post_processing.edit.back_confirm_title"),
      onCancel: this.closeModal,
      children: (
        <div className="text-c">
          <div key={"modal-body"} className="modal-body">
            <span>{t("post_processing.edit.back_confirm_message")}</span>
          </div>
          <ModalFooter
            key={"modal-footer"}
            saveButtonText={t("common.ok")}
            onConfirm={() => {
              this.closeModal();
              this.props.history.push("/postprocessing");
            }}
            cancelButtonText={t("common.cancel")}
            onCancel={this.closeModal}
          />
        </div>
      ),
      type: 'small'
    });
  };

  closeModal = () => {
    const { modalStore } = this.props;
    modalStore.hideAll();
  };

  validateRegex = (regex) => {
    try {
      new RegExp(regex);
      return true;
    } catch (e) {
      return false;
    }
  }

  validateRow = (row) => {
    if ((!row.regex && row.replacement) || (row.regex && !this.validateRegex(row.regex))) {
      return true;
    }
    return false;
  }

  validateAllRows = () => {
    const { commonStore: { postProcessingStore: { editProcess } } } = this.props;
    const validationErrors = {};
    let hasError = false;

    if (editProcess.setting_body) {
      editProcess.setting_body.forEach((row, index) => {
        if (this.validateRow(row)) {
          validationErrors[index] = true;
          hasError = true;
        }
      });
    }

    this.setState({ validationErrors });
    return !hasError;
  }

  handleInputChange = (index, field, value) => {
    const { commonStore: { postProcessingStore } } = this.props;
    postProcessingStore.updateRow(index, field, value);
    this.isEditing = true;
  }

  handleAddRow = (index) => {
    const { commonStore: { postProcessingStore } } = this.props;
    postProcessingStore.addRow(index);
    this.isEditing = true;
  }

  handleAddRowAtEnd = () => {
    const { commonStore: { postProcessingStore } } = this.props;
    postProcessingStore.addRowAtEnd();
    this.isEditing = true;
  }

  handleDeleteRow = (index) => {
    const { commonStore: { postProcessingStore } } = this.props;
    postProcessingStore.deleteRow(index);
    this.isEditing = true;
  }

  handleSwapRows = (index1, index2) => {
    const { commonStore: { postProcessingStore } } = this.props;
    postProcessingStore.swapRows(index1, index2);
    this.isEditing = true;
  }

  handleSave = async () => {
    if (!this.validateAllRows()) {
      return;
    }

    const { commonStore: { postProcessingStore: { editProcess } } } = this.props;
    const formattedBody = editProcess.setting_body.filter(e => e.regex || e.replacement);
    const payload = {
      settingName: editProcess.setting_name,
      data: formattedBody
    };

    const result = await this.props.commonStore.postProcessingStore.updatePostProcess(editProcess.id, payload);
    if (result) {
      this.isEditing = false;
    }
  }

  handleResizeStart = (e) => {
    e.preventDefault();
    this.isDragging = true;
    this.dragStartX = e.clientX;
    this.dragStartRegexWidth = this.state.regexColumnWidth;
    this.dragStartReplacementWidth = this.state.replacementColumnWidth;

    document.addEventListener('mousemove', this.handleResizeMove);
    document.addEventListener('mouseup', this.handleResizeEnd);
  }

  handleResizeMove = (e) => {
    if (!this.isDragging) return;

    const tableElement = document.querySelector('.resizable-post-processing-table table');
    if (!tableElement) return;

    const tableWidth = tableElement.offsetWidth;
    const deltaX = e.clientX - this.dragStartX;
    const deltaPercent = (deltaX / tableWidth) * 100;

    let newRegexWidth = this.dragStartRegexWidth + deltaPercent;
    let newReplacementWidth = this.dragStartReplacementWidth - deltaPercent;

    const minWidth = 12;
    const maxCombinedWidth = this.dragStartRegexWidth + this.dragStartReplacementWidth;

    if (newRegexWidth < minWidth) {
      newRegexWidth = minWidth;
      newReplacementWidth = maxCombinedWidth - minWidth;
    } else if (newReplacementWidth < minWidth) {
      newReplacementWidth = minWidth;
      newRegexWidth = maxCombinedWidth - minWidth;
    }

    this.setState({
      regexColumnWidth: newRegexWidth,
      replacementColumnWidth: newReplacementWidth
    });
  }

  handleResizeEnd = () => {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  componentWillUnmount() {
    if (this.tooltipDebounceTimer) {
      clearTimeout(this.tooltipDebounceTimer);
    }
    document.removeEventListener('mousemove', this.handleResizeMove);
    document.removeEventListener('mouseup', this.handleResizeEnd);
  }

  render() {
    const { t, commonStore: { postProcessingStore: { editProcess } } } = this.props;
    const { validationErrors, regexColumnWidth, replacementColumnWidth } = this.state;
    const settingName = editProcess.setting_name || "";
    const tableData = editProcess.setting_body || [{ regex: '', replacement: '' }];

    const columns = [
      {
        Header: t("post_processing.edit.table.no"),
        disableSortBy: true,
        width: "6%",
        Cell: ({ row }) => <div style={{ textAlign: "center" }}>{row.index + 1}</div>
      },
      {
        Header: () => (
          <div style={{ position: 'relative' }}>
            {t("post_processing.edit.table.regex_pattern")}
            <div
              className="column-resize-handle header-resize"
              onMouseDown={this.handleResizeStart}
            />
          </div>
        ),
        accessor: 'regex',
        disableSortBy: true,
        width: `${regexColumnWidth}%`,
        Cell: ({ row }) => {
          const hasError = validationErrors[row.index];
          return (
            <div style={{ position: 'relative' }}>
              <input
                className="inp-content"
                style={{ width: "100%", borderColor: hasError ? 'red' : undefined }}
                type="text"
                defaultValue={row.original.regex || ""}
                onChange={(e) => this.handleInputChange(row.index, 'regex', e.target.value)}
              />
              {hasError && <div className="err-msg">{t("post_processing.edit.validation.invalid_regex")}</div>}
              <div
                className="column-resize-handle"
                onMouseDown={this.handleResizeStart}
              />
            </div>
          );
        }
      },
      {
        Header: t("post_processing.edit.table.replacement"),
        accessor: 'replacement',
        disableSortBy: true,
        width: `${replacementColumnWidth}%`,
        Cell: ({ row }) => {
          const hasError = validationErrors[row.index];
          return (
            <div>
              <input
                className="inp-content"
                style={{ width: "100%" }}
                type="text"
                defaultValue={row.original.replacement || ""}
                onChange={(e) => this.handleInputChange(row.index, 'replacement', e.target.value)}
              />
              {hasError && <div className="err-msg">&nbsp;</div>}
            </div>
          );
        }
      },
      {
        Header: "",
        id: 'swap',
        disableSortBy: true,
        width: "7%",
        disableResizing: true,
        className: "action-column",
        headerClassName: "header-middle-align",
        Cell: ({ row }) => {
          return (
            <div>
              <i
                className="fas fa-chevron-circle-up fa-1-5x ar-tooltip"
                style={{
                  color: "#377fa8",
                  cursor: row.index <= 0 ? "not-allowed" : "pointer",
                  marginLeft: "0px",
                  marginRight: "5px",
                  opacity: row.index <= 0 ? .65 : 1
                }}
                title={t("post_processing.edit.move_up")}
                onClick={() => row.index > 0 && this.handleSwapRows(row.index, row.index - 1)}>
              </i>
              <i
                className="fas fa-chevron-circle-down fa-1-5x ar-tooltip"
                style={{
                  color: "#377fa8",
                  cursor: row.index + 1 >= tableData.length ? "not-allowed" : "pointer",
                  margin: "0px",
                  opacity: row.index + 1 >= tableData.length ? .65 : 1
                }}
                title={t("post_processing.edit.move_down")}
                onClick={() => row.index + 1 < tableData.length && this.handleSwapRows(row.index, row.index + 1)}>
              </i>
            </div>
          );
        }
      },
      {
        Header: "",
        id: 'action',
        disableSortBy: true,
        width: "7%",
        disableResizing: true,
        className: "action-column",
        headerClassName: "header-middle-align",
        Cell: ({ row }) => {
          return (
            <div>
              <i
                className="fas fa-plus-circle fa-1-5x ar-tooltip"
                style={{ color: "#377fa8", cursor: "pointer", marginLeft: "0px", marginRight: "5px" }}
                title={t("post_processing.edit.add_row")}
                onClick={() => this.handleAddRow(row.index)}>
              </i>
              <i
                className="fas fa-minus-circle fa-1-5x ar-tooltip"
                style={{ color: "#dd4b39", cursor: "pointer", margin: "0px" }}
                title={t("post_processing.edit.delete_row")}
                onClick={() => this.handleDeleteRow(row.index)}>
              </i>
            </div>
          );
        }
      }
    ];

    return (
      <div className="screen-post-processing">
        <ContainerBox headerTitle={`${t('post_processing.edit.page_title')}`}>
          <div className="admin-header-container">
            <div className="col-sm-8">
              <div className="row">
                <div className="col-sm-4" style={{ textAlign: "left", fontWeight: "bold" }}>
                  {t("post_processing.edit.setting_name")}
                </div>
                <div className="col-sm-8" style={{ textAlign: "left" }}>
                  {settingName}
                </div>
              </div>
            </div>
            <div className="col-sm-4" style={{ textAlign: "right", display: "inline-block" }}>
              <button
                className="btn btn-primary f-r mb"
                type="button"
                style={{ marginLeft: "10px" }}
                onClick={this.handleSave}
              >
                {t("post_processing.edit.save_button")}
              </button>
              <button
                className="btn btn-primary f-r mb"
                type="button"
                style={{ marginLeft: "10px" }}
                onClick={this.handleBack}
              >
                {t("post_processing.edit.back_button")}
              </button>
            </div>
          </div>
        </ContainerBox>

        <ContainerBox type="basic" className="admin-edit-body-container table-container">
          <div className="box-body document-item-info resizable-post-processing-table">
            <Table
              columns={columns}
              data={tableData}
              disablePaging={true}
              enableServerSidePaging={false}
              disableSelectionPageSize={true}
              className="tbl-header"
              tableFooter={
                <tr>
                  <td colSpan={columns.length}>
                    <i
                      className="fas fa-plus-circle fa-1-5x ar-tooltip"
                      style={{ color: "#377fa8", cursor: "pointer" }}
                      title={t("post_processing.edit.add_row")}
                      onClick={() => this.handleAddRowAtEnd()}>
                    </i>
                  </td>
                </tr>
              }
            />
          </div>
        </ContainerBox>
      </div>
    );
  }
}

export default withRouter(withTranslation()(EditPostProcessing));
